import React from "react";
import { get } from "../../utils";
import {
  downloadExcel,
  ExcelColumn,
  SingleTableBody,
  stampExportName,
} from "../../utils/excel-download";
import { Button, CircularProgress } from "@material-ui/core";

export type DataColumn = ExcelColumn;

export interface TableBoundary {
  currentHeight: number;
  totalHeight: number;
}

interface DataTableProps {
  title?: string;
  /**
   * Base filename for the Excel export, WITHOUT the datetime stamp or extension
   * (e.g. "LAMPPOST_INV_24513"). When provided, the download datetime is
   * appended at click time and the final name becomes
   * "LAMPPOST_INV_24513_202605281411.xlsx". When omitted, the server falls
   * back to naming the file after `title` (legacy behaviour).
   */
  exportName?: string;
  columns: DataColumn[];
  data: any[];
  allData?: any[];
  type?: string;
  onClick?: (row: any, unselect?: boolean) => void;
  onScroll?: (bound: TableBoundary) => void;
}

export const DataTable: React.FC<DataTableProps> = (props) => {
  const { title, exportName, columns, data, allData, type, onClick, onScroll } = props;

  const [loading, setLoading] = React.useState<boolean>(false);

  const ref = React.useRef<HTMLTableSectionElement>(null);

  const handleOnScroll = (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const { scrollHeight, scrollTop } = event.currentTarget;
    if (onScroll) {
      onScroll({ totalHeight: scrollHeight, currentHeight: scrollTop });
    }
  };

  const handleOnClickDownloadButton = (title: string = 'data', columns: DataColumn[], data: any[]) => {
    setLoading(true);

    const body: SingleTableBody = { title, columns, data };

    // Option A: the client owns the filename. The "download datetime" is
    // stamped at click time so it reflects when Export was pressed. The
    // extension (.xlsx / .zip) is appended by the server, which is why the
    // final name is read back from the Content-Disposition header.
    if (exportName) {
      body.filename = stampExportName(exportName);
    }

    downloadExcel(body)
      .catch(err => console.error(err))
      .finally(() => {
        setLoading(false);
      })
  };

  return (
    <div className={type || "portal-datatable-no-overflow"} onScroll={handleOnScroll}>
      <table cellPadding={5} cellSpacing={0}>
      <caption style={{ marginBottom: '5px' }}>
            {title}

            <Button
              variant="contained"
              style={title ? { marginLeft: '10px' } : {}}
              size="small"
              onClick={(evt: any) => handleOnClickDownloadButton(title, columns, (allData ? allData : data))}
              disabled={loading}
            >
              Export
              {
                loading && (
                  <CircularProgress
                    size={24}
                    style={{
                      color: 'green',
                      textAlign: 'center',
                      position: 'absolute'
                    }}
                  />
                )
              }
            </Button>
          </caption>

        <thead>
          <tr>
            {columns.map((item) => (
              <th key={item.title}>{item.title}</th>
            ))}
          </tr>
        </thead>
        <tbody ref={ref}>
          {data.map((row, index) => (
            <tr
              key={index}
              onClick={(evt) => {
                const { classList } = evt.currentTarget;
                if (onClick) {
                  if (classList.contains("selected")) {
                    classList.remove("selected");
                    onClick(row, true);
                  } else {
                    if (ref.current) {
                      ref.current
                        .querySelectorAll("tr.selected")
                        .forEach((row) => row.classList.remove("selected"));
                    }
                    classList.add("selected");
                    onClick(row);
                  }
                }
              }}
            >
              {columns.map((key) => (
                <td key={key.dataIndex}>{get(row, key.dataIndex)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
